import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout.jsx";
import { masterSupabase, getUserRole } from "../lib/supabase.js";

// ═══ Agents — the console for the platform's autonomous workers ═══
// Tabbed (5 Oct 2026): Agents (roster cards) · Findings (steward review
// queue) · Briefs (Insight Agent's weekly reports). Tab badges carry the
// counts so nothing is hidden, just organized.

const sans = "'DM Sans', -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";
const C = { ink: "#1a1a1a", dim: "#8a8a8a", faint: "#c0bdb8", line: "#e5e3de", bg: "#fafaf8", card: "#fff", accent: "#6366f1", red: "#dc2626", amber: "#d97706", green: "#16a34a" };

async function api(path, options = {}) {
  const { data: { session } } = await masterSupabase.auth.getSession();
  const res = await fetch(path, {
    ...options,
    headers: { Authorization: `Bearer ${session?.access_token}`, "Content-Type": "application/json", ...(options.headers || {}) },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json.data;
}
const dataOp = (op) => api("/api/data", { method: "POST", body: JSON.stringify(op) });

const SEV_COLOR = { high: C.red, medium: C.amber, low: C.dim };
const TYPE_LABEL = {
  website_dead: "Website down",
  website_redirects_offsite: "Redirects elsewhere",
  website_unverifiable: "Blocks checks",
  bad_website_value: "Bad website value",
  missing_coordinates: "No map pin",
  bad_phone: "Phone looks wrong",
  possible_duplicate: "Possible duplicates",
};

function timeAgo(iso) {
  if (!iso) return "never";
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / 1440)}d ago`;
}

function findingLine(f) {
  const d = f.detail || {};
  switch (f.finding_type) {
    case "website_dead": return d.final_url?.includes("godaddy") ? "Domain is parked / for sale" : d.status ? `Site returns HTTP ${d.status}` : (d.error || "Unreachable");
    case "website_redirects_offsite": return `Sends visitors to ${d.final_apex || d.final_url || "another domain"}`;
    case "website_unverifiable": return `Site refuses automated checks (HTTP ${d.status})`;
    case "bad_website_value": return `Not a web address: ${d.url || d.scheme || ""}`;
    case "missing_coordinates": return "No usable latitude/longitude — won't appear as a pin";
    case "bad_phone": return `"${d.phone}" doesn't look like a valid phone number`;
    case "possible_duplicate": return `${d.group_size || (d.members?.length)} entries that look like the same place`;
    default: return "";
  }
}

export default function Agents() {
  const role = getUserRole();
  const canAct = ["owner", "admin", "editor"].includes(role);
  const [tab, setTab] = useState("agents");
  const [agents, setAgents] = useState(null);
  const [findings, setFindings] = useState([]);
  const [locNames, setLocNames] = useState({});
  const [briefs, setBriefs] = useState([]);
  const [posture, setPosture] = useState([]);
  const [briefIdx, setBriefIdx] = useState(0);
  const [running, setRunning] = useState(null);
  const [busy, setBusy] = useState({});
  const [error, setError] = useState(null);
  const [sevFilter, setSevFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const [roster, open, locs, briefRows, postureRows] = await Promise.all([
        api("/api/agents"),
        dataOp({ op: "select", table: "location_findings", filters: { status: "open" }, order: [{ column: "severity" }, { column: "last_seen", ascending: false }], limit: 500 }),
        dataOp({ op: "select", table: "locations", columns: "id, name" }),
        dataOp({ op: "select", table: "agent_insights", columns: "id, period_start, period_end, headline, sections, actions, model, created_at", order: { column: "created_at", ascending: false }, limit: 12 }),
        dataOp({ op: "select", table: "agent_findings", filters: { status: "open" }, order: [{ column: "severity" }, { column: "last_seen", ascending: false }], limit: 200 }),
      ]);
      setAgents(roster);
      setFindings(open || []);
      setLocNames(Object.fromEntries((locs || []).map((l) => [l.id, l.name])));
      setBriefs(briefRows || []); setBriefIdx(0);
      setPosture(postureRows || []);
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runNow = async (slug) => {
    setRunning(slug); setError(null);
    try { await api("/api/agents", { method: "POST", body: JSON.stringify({ slug }) }); await load(); }
    catch (e) { setError(`Run failed: ${e.message}`); }
    setRunning(null);
  };

  const toggleAgent = async (a) => {
    try {
      await dataOp({ op: "update", table: "agents", filters: { slug: a.slug }, values: { enabled: !a.enabled, updated_at: new Date().toISOString() } });
      await load();
    } catch (e) { setError(e.message); }
  };

  const dismissPosture = async (f) => {
    setBusy((p) => ({ ...p, [f.id]: true }));
    try {
      await dataOp({ op: "update", table: "agent_findings", id: f.id, values: { status: "dismissed", dismissed_at: new Date().toISOString(), dismissed_by: "dashboard" } });
      setPosture((p) => p.filter((x) => x.id !== f.id));
    } catch (e) { setError(e.message); }
    setBusy((p) => { const n = { ...p }; delete n[f.id]; return n; });
  };

  const dismiss = async (f) => {
    setBusy((p) => ({ ...p, [f.id]: true }));
    try {
      await dataOp({ op: "update", table: "location_findings", id: f.id, values: { status: "dismissed", dismissed_at: new Date().toISOString(), dismissed_by: "dashboard" } });
      setFindings((p) => p.filter((x) => x.id !== f.id));
    } catch (e) { setError(e.message); }
    setBusy((p) => { const n = { ...p }; delete n[f.id]; return n; });
  };

  const statusDot = (a) => {
    if (!a.enabled) return { color: C.faint, label: "Paused" };
    const r = a.last_run;
    if (!r) return { color: C.amber, label: "No runs yet" };
    if (r.status === "failed") return { color: C.red, label: "Last run failed" };
    const hours = (Date.now() - new Date(r.started_at).getTime()) / 3600000;
    return hours < 24 * 8 ? { color: C.green, label: "Healthy" } : { color: C.amber, label: "Heartbeat overdue" };
  };

  const shown = sevFilter === "all" ? findings : findings.filter((f) => f.severity === sevFilter);
  const sevCounts = findings.reduce((m, f) => ({ ...m, [f.severity]: (m[f.severity] || 0) + 1 }), {});

  const TABS = [
    { id: "agents", label: "Agents", badge: agents?.length ?? "…" },
    { id: "findings", label: "Findings", badge: findings.length + posture.length },
    { id: "briefs", label: "Weekly briefs", badge: briefs.length },
  ];

  return (
    <Layout>
      <div style={{ fontFamily: sans, color: C.ink, maxWidth: 1000 }}>
        <div style={{ marginBottom: 6 }}>
          <h1 style={{ margin: 0, fontFamily: serif, fontStyle: "italic", fontWeight: 400, fontSize: 34 }}>Agents</h1>
          <div style={{ color: C.dim, fontSize: 13, marginTop: 4 }}>Autonomous workers that watch your data around the clock and file what they find for your review.</div>
        </div>

        {/* ── Tabs ── */}
        <div style={{ display: "flex", gap: 2, borderBottom: `1px solid ${C.line}`, margin: "22px 0 24px" }}>
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ background: "none", border: "none", borderBottom: tab === t.id ? `2px solid ${C.ink}` : "2px solid transparent", padding: "10px 16px", fontSize: 13.5, fontWeight: tab === t.id ? 700 : 500, color: tab === t.id ? C.ink : C.dim, cursor: "pointer", fontFamily: sans, display: "flex", alignItems: "center", gap: 7, marginBottom: -1 }}>
              {t.label}
              <span style={{ background: tab === t.id ? C.ink : "#eceae5", color: tab === t.id ? "#fff" : C.dim, borderRadius: 10, padding: "1px 8px", fontSize: 10.5, fontWeight: 700 }}>{t.badge}</span>
            </button>
          ))}
        </div>

        {error && <div style={{ margin: "0 0 14px", padding: "10px 14px", borderRadius: 8, fontSize: 13, background: "#fef2f2", color: C.red, border: "1px solid #fecaca" }}>{error}</div>}

        {/* ═══ TAB: Agents ═══ */}
        {tab === "agents" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))", gap: 16 }}>
            {agents === null && <div style={{ color: C.dim, fontSize: 13 }}>Loading…</div>}
            {agents?.length === 0 && <div style={{ color: C.dim, fontSize: 13 }}>No agents registered yet.</div>}
            {(agents || []).map((a) => {
              const dot = statusDot(a);
              const sum = a.last_run?.summary || {};
              const openTotal = sum.open_by_type ? Object.values(sum.open_by_type).reduce((x, y) => x + y, 0) : null;
              return (
                <div key={a.slug} style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, padding: 22 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ width: 9, height: 9, borderRadius: "50%", background: dot.color, display: "inline-block" }} />
                        <span style={{ fontSize: 17, fontWeight: 700 }}>{a.name}</span>
                      </div>
                      <div style={{ fontSize: 11, color: C.dim, marginTop: 3 }}>{dot.label} · {a.schedule_label || "manual"} · last ran {timeAgo(a.last_run?.started_at)}</div>
                    </div>
                    {canAct && (
                      <button onClick={() => toggleAgent(a)} title={a.enabled ? "Pause this agent" : "Resume this agent"}
                        style={{ width: 38, height: 21, borderRadius: 11, border: "none", cursor: "pointer", background: a.enabled ? C.green : C.faint, position: "relative", flexShrink: 0 }}>
                        <span style={{ width: 15, height: 15, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: a.enabled ? 20 : 3, transition: "left 0.15s" }} />
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: 12.5, color: "#555", lineHeight: 1.5, margin: "12px 0 14px" }}>{a.description}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                    {sum.locations != null && <Stat label="locations watched" value={sum.locations} />}
                    {openTotal != null && <Stat label="open findings" value={openTotal} />}
                    {sum.drafts_created != null && <Stat label="drafts filed" value={sum.drafts_created} />}
                    {sum.sections != null && <Stat label="brief sections" value={sum.sections} />}
                    {sum.runtime_ms != null && <Stat label="last run" value={`${(sum.runtime_ms / 1000).toFixed(1)}s`} />}
                    <div style={{ flex: 1 }} />
                    {a.slug === "location-steward" && <OutLink onClick={() => setTab("findings")} label="Findings tab" />}
                    {a.slug === "compliance-agent" && <OutLink onClick={() => setTab("findings")} label="Findings tab" />}
                    {a.slug === "insight-agent" && <OutLink onClick={() => setTab("briefs")} label="Weekly briefs" />}
                    {a.slug === "content-agent" && <OutLink onClick={() => window.location.assign("/training")} label="Training queue" />}
                    {a.slug === "lead-shepherd" && <OutLink onClick={() => window.location.assign("/leads")} label="Leads page" />}
                    {a.slug === "knowledge-librarian" && <OutLink onClick={() => setTab("findings")} label="Findings tab" />}
                    {canAct && a.runnable && (
                      <button onClick={() => runNow(a.slug)} disabled={running === a.slug || !a.enabled}
                        style={{ background: C.ink, color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 12.5, fontWeight: 600, cursor: running === a.slug ? "default" : "pointer", fontFamily: sans, opacity: running === a.slug || !a.enabled ? 0.5 : 1 }}>
                        {running === a.slug ? "Running…" : "Run now"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ═══ TAB: Findings ═══ */}
        {tab === "findings" && (
          <>
            {/* Compliance Agent group */}
            <GroupHeader agent="Compliance Agent" note="privacy & configuration posture" count={posture.length} />
            <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, overflow: "hidden", marginBottom: 28 }}>
              {posture.length === 0 && <div style={{ padding: 24, textAlign: "center", color: C.dim, fontSize: 13 }}>No posture issues open. The Compliance Agent found nothing to flag.</div>}
              {posture.map((f, i) => (
                <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "13px 18px", borderTop: i > 0 ? `1px solid ${C.line}` : "none" }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: SEV_COLOR[f.severity] || C.dim, flexShrink: 0 }} title={f.severity} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600 }}>{f.title}</div>
                    {f.detail?.note && <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>{f.detail.note}</div>}
                  </div>
                  <div style={{ fontSize: 10.5, color: C.faint, flexShrink: 0 }}>seen {timeAgo(f.last_seen)}</div>
                  {canAct && (
                    <button onClick={() => dismissPosture(f)} disabled={busy[f.id]}
                      style={{ background: "none", color: C.dim, border: `1px solid ${C.line}`, borderRadius: 7, padding: "6px 12px", fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: sans, flexShrink: 0, opacity: busy[f.id] ? 0.5 : 1 }}>
                      Dismiss
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Location Steward group */}
            <GroupHeader agent="Location Steward" note="dealer map data quality" count={findings.length} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginBottom: 12 }}>
              {["all", "high", "medium", "low"].map((s) => (
                <button key={s} onClick={() => setSevFilter(s)}
                  style={{ background: sevFilter === s ? C.ink : "none", color: sevFilter === s ? "#fff" : C.dim, border: `1px solid ${sevFilter === s ? C.ink : C.line}`, borderRadius: 20, padding: "4px 12px", fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: sans }}>
                  {s === "all" ? `All (${findings.length})` : `${s} (${sevCounts[s] || 0})`}
                </button>
              ))}
            </div>
            <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, overflow: "hidden" }}>
              {shown.length === 0 && <div style={{ padding: 36, textAlign: "center", color: C.dim, fontSize: 13 }}>Nothing awaiting review{sevFilter !== "all" ? ` at ${sevFilter} severity` : ""}. The steward is on patrol.</div>}
              {shown.map((f, i) => (
                <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "13px 18px", borderTop: i > 0 ? `1px solid ${C.line}` : "none" }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: SEV_COLOR[f.severity] || C.dim, flexShrink: 0 }} title={f.severity} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                      {locNames[f.location_id] || "(unknown location)"}
                      <span style={{ fontWeight: 400, color: C.dim }}> — {TYPE_LABEL[f.finding_type] || f.finding_type}</span>
                    </div>
                    <div style={{ fontSize: 12, color: "#555", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {findingLine(f)}
                      {f.detail?.url && <a href={f.detail.url} target="_blank" rel="noopener" style={{ color: C.accent, marginLeft: 8 }}>open link</a>}
                    </div>
                  </div>
                  <div style={{ fontSize: 10.5, color: C.faint, flexShrink: 0 }}>seen {timeAgo(f.last_seen)}</div>
                  {canAct && (
                    <button onClick={() => dismiss(f)} disabled={busy[f.id]}
                      style={{ background: "none", color: C.dim, border: `1px solid ${C.line}`, borderRadius: 7, padding: "6px 12px", fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: sans, flexShrink: 0, opacity: busy[f.id] ? 0.5 : 1 }}>
                      Dismiss
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: C.faint, marginTop: 14, lineHeight: 1.6 }}>
              Dismissing a finding tells the agent it's reviewed — it won't re-file unless the problem changes. Fix the underlying
              data (on the Locations page) and the finding resolves itself on the agent's next patrol.
            </div>
          </>
        )}

        {/* ═══ TAB: Weekly briefs ═══ */}
        {tab === "briefs" && (<>
          <GroupHeader agent="Insight Agent" note="the Monday operations brief, written from chat, Google, map, and findings data" count={briefs.length} />
          {
          briefs.length === 0 ? (
            <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, padding: 36, textAlign: "center", color: C.dim, fontSize: 13 }}>
              No briefs yet — the Insight Agent writes one every Monday morning, or run it now from the Agents tab.
            </div>
          ) : (() => { const b = briefs[briefIdx]; return (
            <div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                {briefs.map((x, i) => (
                  <button key={x.id} onClick={() => setBriefIdx(i)}
                    style={{ background: i === briefIdx ? C.ink : "none", color: i === briefIdx ? "#fff" : C.dim, border: `1px solid ${i === briefIdx ? C.ink : C.line}`, borderRadius: 20, padding: "4px 12px", fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: sans }}>
                    {new Date(x.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </button>
                ))}
              </div>
              <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, padding: "26px 28px" }}>
                <div style={{ fontSize: 10.5, color: C.faint, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
                  {b.period_start} → {b.period_end}
                </div>
                <div style={{ fontFamily: serif, fontStyle: "italic", fontSize: 21, lineHeight: 1.35, marginBottom: 18 }}>{b.headline}</div>
                {(b.sections || []).map((sec, i) => (
                  <div key={i} style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: C.dim, marginBottom: 3 }}>{sec.title}</div>
                    <div style={{ fontSize: 13.5, lineHeight: 1.6, color: "#333" }}>{sec.body}</div>
                  </div>
                ))}
                {(b.actions || []).length > 0 && (
                  <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${C.line}` }}>
                    <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: C.dim, marginBottom: 8 }}>Recommended actions</div>
                    {(b.actions || []).map((act, i) => (
                      <div key={i} style={{ display: "flex", gap: 10, fontSize: 13.5, lineHeight: 1.6, color: "#333", marginBottom: 5 }}>
                        <span style={{ color: C.accent, fontWeight: 700 }}>{i + 1}.</span><span>{act}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: 10.5, color: C.faint, marginTop: 16 }}>Written by the Insight Agent · {b.model}</div>
              </div>
            </div>
          ); })()
          }</>
        )}
      </div>
    </Layout>
  );
}

function GroupHeader({ agent, note, count }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
      <span style={{ fontFamily: serif, fontStyle: "italic", fontSize: 19 }}>{agent}</span>
      <span style={{ fontSize: 11.5, color: C.dim }}>{note}</span>
      <span style={{ background: "#eceae5", color: C.dim, borderRadius: 10, padding: "1px 8px", fontSize: 10.5, fontWeight: 700 }}>{count}</span>
    </div>
  );
}

function OutLink({ onClick, label }) {
  return (
    <button onClick={onClick}
      style={{ background: "none", border: "none", color: C.accent, fontSize: 11.5, fontWeight: 700, cursor: "pointer", padding: 0, fontFamily: sans }}>
      Output → {label}
    </button>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 17, fontWeight: 700, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 10, color: C.dim, textTransform: "uppercase", letterSpacing: "0.07em", marginTop: 3 }}>{label}</div>
    </div>
  );
}
